'use client'

import {
  Accordion,
  AccordionItem,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Column,
  Grid,
  InlineNotification,
  Layer,
  Link,
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
  Tag,
  Theme,
} from '@carbon/react'
import { Catalog, Checkmark, CurrencyDollar, Help, Information, Launch, LogoGithub } from '@carbon/react/icons'
import type { CatalogItem } from '@/lib/catalog'
import { isCatalogUnavailable } from '@/lib/catalog'
import { getCatalogStatus } from '@/lib/catalog-availability'
import { CatalogPricingSections } from '@/components/catalog-pricing-sections'
import { PanelDemoSection } from '@/components/panel-demo-section'
import { PaymentMethodsStrip } from '@/components/payment-methods-strip'
import { TrustStandardsStrip } from '@/components/trust-standards-strip'
import { catalogUiCopy } from '@/lib/catalog-ui-i18n'
import { homeCatalogDescription } from '@/lib/home-catalog-i18n'
import { homeCatalogName } from '@/lib/home-catalog-names-i18n'

type CatalogDetailPageProps = {
  item: CatalogItem
  kind: 'service' | 'product'
  locale?: string
}

function offersFor(item: CatalogItem) {
  if (isCatalogUnavailable(item.status) || isCatalogUnavailable(getCatalogStatus(item))) return []
  return item.pricing?.filter((group) => !isCatalogUnavailable(group.status)).flatMap((group) => group.plans
    .filter((plan) => plan.priceValue !== undefined)
    .map((plan) => ({
      '@type': 'Offer',
      name: `${group.title} - ${plan.name}`,
      price: Number(plan.priceValue),
      priceCurrency: 'USD',
      url: group.externalUrl ?? item.externalUrl ?? `https://kmerhosting.com/services/${item.slug}`,
    }))) ?? []
}

function breadcrumbStructuredData(item: CatalogItem, kind: CatalogDetailPageProps['kind'], canonicalUrl: string) {
  const collection = kind === 'service' ? 'Services' : 'Products'
  const collectionUrl = kind === 'service' ? 'https://kmerhosting.com/services' : 'https://kmerhosting.com/#products'
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'KmerHosting', item: 'https://kmerhosting.com/' },
      { '@type': 'ListItem', position: 2, name: collection, item: collectionUrl },
      { '@type': 'ListItem', position: 3, name: item.name, item: canonicalUrl },
    ],
  }
}

export function CatalogDetailPage({ item: catalogItem, kind, locale = 'en' }: CatalogDetailPageProps) {
  const copy = catalogUiCopy(locale)
  const item = {
    ...catalogItem,
    name: homeCatalogName(locale as Parameters<typeof homeCatalogName>[0], catalogItem.slug, catalogItem.name),
    description: homeCatalogDescription(locale as Parameters<typeof homeCatalogDescription>[0], catalogItem.slug, catalogItem.description),
  }
  const isService = kind === 'service'
  const canonicalUrl = `https://kmerhosting.com/${isService ? 'services' : 'products'}/${item.slug}`
  const itemStatus = getCatalogStatus(item)
  const isUnavailable = isCatalogUnavailable(itemStatus)
  const isComingSoon = itemStatus === 'coming-soon'
  const isOutOfStock = itemStatus === 'out-of-stock'
  const featureGroups = item.featureGroups
  const offers = isService ? offersFor(item) : []
  const structuredData = isService
    ? {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: item.name,
        description: item.description,
        url: canonicalUrl,
        provider: { '@type': 'Organization', name: 'KmerHosting', url: 'https://kmerhosting.com' },
        serviceType: item.name,
        ...(offers.length > 0 ? { offers } : {}),
      }
    : {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: item.name,
        description: item.description,
        url: canonicalUrl,
        applicationCategory: 'BusinessApplication',
        provider: { '@type': 'Organization', name: 'KmerHosting', url: 'https://kmerhosting.com' },
        sameAs: [item.websiteUrl, item.externalUrl].filter(Boolean),
      }
  const breadcrumbData = breadcrumbStructuredData(item, kind, canonicalUrl)

  return (
    <main id="main-content" className="page-main catalog-detail" itemScope itemType={isService ? 'https://schema.org/Service' : 'https://schema.org/SoftwareApplication'}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData) }} />

      <section className="catalog-detail__hero" aria-labelledby="catalog-title">
        <Grid>
          <Column sm={4} md={8} lg={12}>
            <Breadcrumb noTrailingSlash className="catalog-detail__breadcrumb">
              <BreadcrumbItem href="/">KmerHosting</BreadcrumbItem>
              <BreadcrumbItem href={isService ? '/services' : '/#products'}>{isService ? copy.services : copy.products}</BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>{item.name}</BreadcrumbItem>
            </Breadcrumb>
          </Column>
          <Column sm={4} md={6} lg={10}>
            {isUnavailable ? (
              <Tag type={isComingSoon ? 'blue' : 'red'}>
                {isComingSoon ? copy.viewRoadmap : copy.availabilitySupport}
              </Tag>
            ) : null}
            <h1 id="catalog-title" className="catalog-detail__title" itemProp="name">{item.name}</h1>
            <p className="catalog-detail__subtitle" itemProp="description">{item.description}</p>
            <div className="hero__actions">
              {isComingSoon ? (
                <Button href="/roadmap" renderIcon={Catalog}>{copy.viewRoadmap}</Button>
              ) : isOutOfStock ? (
                <Button href="/support" renderIcon={Help}>{copy.availabilitySupport}</Button>
              ) : isService && item.externalUrl ? (
                <Button href={item.externalUrl} target="_blank" rel="noreferrer" renderIcon={Launch}>
                  {copy.openServicePortal}
                </Button>
              ) : null}
              {!isUnavailable && isService && !item.externalUrl && item.pricing ? (
                <Button href="#pricing" kind="tertiary" renderIcon={CurrencyDollar}>{copy.viewPricing}</Button>
              ) : null}
              {!isUnavailable && !isService && item.websiteUrl ? (
                <Button href={item.websiteUrl} target="_blank" rel="noreferrer" renderIcon={Launch}>{copy.openProductWebsite}</Button>
              ) : null}
              {!isUnavailable && !isService && item.slug !== 'convertsuite' && item.externalUrl ? (
                <Button href={item.externalUrl} target="_blank" rel="noreferrer" kind={item.websiteUrl ? 'tertiary' : 'primary'} renderIcon={LogoGithub}>{copy.viewGithub}</Button>
              ) : null}
            </div>
          </Column>
        </Grid>
      </section>

      <PanelDemoSection item={item} locale={locale} />

      <section className="section catalog-detail__body" aria-labelledby="benefits-title">
        <Grid>
          <Column sm={4} md={4} lg={5}>
            <p className="section-label">{copy.whoItIsFor}</p>
            <h2 id="benefits-title" className="section__title">{isService ? copy.whyChoose : copy.whatItDoes}</h2>
            <p className="section__subtitle" itemProp="audience">{item.audience}</p>
          </Column>
          <Column sm={4} md={4} lg={{ span: 10, offset: 1 }}>
            <Layer>
              <StructuredListWrapper aria-label={`${item.name} benefits`}>
                <StructuredListBody>
                  {item.benefits.map((benefit) => (
                    <StructuredListRow key={benefit}>
                      <StructuredListCell className="catalog-benefit__icon"><Checkmark size={20} aria-hidden="true" /></StructuredListCell>
                      <StructuredListCell><strong>{benefit}</strong></StructuredListCell>
                    </StructuredListRow>
                  ))}
                </StructuredListBody>
              </StructuredListWrapper>
            </Layer>
          </Column>
        </Grid>
      </section>

      <section className="section catalog-features" aria-labelledby="features-title">
        <Grid>
          <Column sm={4} md={4} lg={5}>
            <p className="section-label">{copy.features}</p>
            <h2 id="features-title" className="section__title">{copy.whatIncluded}</h2>
            <p className="section__subtitle">{copy.reviewFeatures}</p>
          </Column>
          <Column sm={4} md={4} lg={{ span: 10, offset: 1 }}>
            <Accordion size="lg">
              {featureGroups.map((group, index) => (
                <AccordionItem key={group.title} title={group.title} open={index === 0}>
                  <StructuredListWrapper isCondensed aria-label={`${group.title} features`}>
                    <StructuredListBody>
                      {group.items.map((feature) => (
                        <StructuredListRow key={feature}>
                          <StructuredListCell className="catalog-feature__icon"><Checkmark size={16} aria-hidden="true" /></StructuredListCell>
                          <StructuredListCell>{feature}</StructuredListCell>
                        </StructuredListRow>
                      ))}
                    </StructuredListBody>
                  </StructuredListWrapper>
                </AccordionItem>
              ))}
            </Accordion>
          </Column>
        </Grid>
      </section>

      {isService ? (
        <section className="section products-section" aria-labelledby="service-scope-title">
          <Grid>
            <Column sm={4} md={4} lg={5}>
              <p className="section-label">{copy.serviceScope}</p>
              <h2 id="service-scope-title" className="section__title">{copy.beforeOrder}</h2>
              <p className="section__subtitle">{copy.scopeSubtitle}</p>
            </Column>
            <Column sm={4} md={4} lg={{ span: 10, offset: 1 }}>
              <Layer>
                <StructuredListWrapper aria-label="KmerHosting service policy links">
                  <StructuredListBody>
                    <StructuredListRow>
                      <StructuredListCell><Information size={20} aria-hidden="true" /></StructuredListCell>
                      <StructuredListCell><strong>{copy.infrastructure}</strong><p>{copy.infrastructureBody}</p><Link href="/infrastructure">{copy.reviewProvider}</Link></StructuredListCell>
                    </StructuredListRow>
                    <StructuredListRow>
                      <StructuredListCell><Information size={20} aria-hidden="true" /></StructuredListCell>
                      <StructuredListCell><strong>{copy.sla}</strong><p>{copy.slaBody}</p><Link href="/sla">{copy.reviewLevels}</Link></StructuredListCell>
                    </StructuredListRow>
                    <StructuredListRow>
                      <StructuredListCell><Help size={20} aria-hidden="true" /></StructuredListCell>
                      <StructuredListCell><strong>{copy.support}</strong><p>{copy.supportBody}</p><Link href="/support">{copy.reviewSupport}</Link></StructuredListCell>
                    </StructuredListRow>
                  </StructuredListBody>
                </StructuredListWrapper>
              </Layer>
            </Column>
          </Grid>
        </section>
      ) : null}

      <CatalogPricingSections item={item} locale={locale} />

      {item.serviceNote ? (
        <section className="section catalog-detail__service-note" aria-label={copy.serviceNote}>
          <Grid>
            <Column sm={4} md={8} lg={12}>
              <InlineNotification kind="info" lowContrast hideCloseButton title={copy.serviceNote} subtitle={item.serviceNote} />
            </Column>
          </Grid>
        </section>
      ) : null}

      <Theme theme="g100">
        <section id="get-started" className="cta-section catalog-detail__cta" aria-labelledby="get-started-title">
          <Grid>
            <Column sm={4} md={6} lg={10}>
            <h2 id="get-started-title">{isComingSoon ? `${item.name} ${copy.roadmapTitle}` : isOutOfStock ? `${item.name} ${copy.outOfStockTitle}` : `${copy.readyTitle} ${item.name}?`}</h2>
              <p>{isComingSoon ? copy.roadmapBody : isOutOfStock ? copy.outOfStockBody : isService ? copy.serviceBody : copy.productBody}</p>
              {isComingSoon ? (
                <Button href="/roadmap" renderIcon={Catalog}>{copy.viewRoadmap}</Button>
              ) : isOutOfStock ? (
                <Button href="/support" renderIcon={Help}>{copy.supportOptions}</Button>
              ) : isService && item.externalUrl ? (
                <Button href={item.externalUrl} target="_blank" rel="noreferrer" renderIcon={Launch}>
                  {copy.openServicePortal}
                </Button>
              ) : isService ? (
                <Button href="/support" renderIcon={Help}>{copy.supportOptions}</Button>
              ) : null}
              {!isUnavailable && !isService && item.websiteUrl ? (
                <Button href={item.websiteUrl} target="_blank" rel="noreferrer" renderIcon={Launch}>{copy.openProductWebsite}</Button>
              ) : null}
              {!isUnavailable && !isService && item.slug !== 'convertsuite' && item.externalUrl ? (
                <Button href={item.externalUrl} target="_blank" rel="noreferrer" kind={item.websiteUrl ? 'tertiary' : 'primary'} renderIcon={LogoGithub}>{copy.viewGithub}</Button>
              ) : null}
            </Column>
          </Grid>
        </section>
      </Theme>

      <PaymentMethodsStrip />
      <TrustStandardsStrip />
    </main>
  )
}
